import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class BaseApiService {
    private readonly _formHttpOptions = {
        headers: new HttpHeaders({
            'Content-Disposition': 'multipart/form-data'
        })
    };

    constructor(private httpClient: HttpClient) {}

    /**
     * Use the Get method when calling an API endpoint that requires both the base and the specific route to the resource you're attempting to access.
     * e.g. baseApiUrl = https://uat.lawyerly.co.za/case-api/
     * e.g. apiRoute = v1/types/areaOfLaw
     * @template T
     * @param baseApiUrl
     * @param apiRoute
     * @returns get
     * Pass in optional HttpParams to append parameters onto the query string, if an object is supplied the key value pairs are created from all defined properties.
     */
    public Get<T>(
        baseApiUrl: string,
        apiRoute: string,
        params?: HttpParams | any
    ): Observable<T> {
        let url = this.addQueryParams(`${baseApiUrl + apiRoute}`, params);
        return this.httpClient.get<T>(url);
    }

    /**
     * Custom Get is used when you have a full URL (this would include the base + route)
     * @template T
     * @param url
     * @returns get
     */
    public CustomGet<T>(url: string): Observable<any> {
        return this.httpClient.get<T>(url);
    }

    public Post<T>(
        baseApiUrl: string,
        apiRoute: string,
        payload: any
    ): Observable<T> {
        return this.httpClient.post<T>(baseApiUrl + apiRoute, payload);
    }

    public Put<T>(
        baseApiUrl: string,
        apiRoute: string,
        payload?: any
    ): Observable<T> {
        return this.httpClient.put<T>(baseApiUrl + apiRoute, payload);
    }

    public Patch<T>(
        baseApiUrl: string,
        apiRoute: string,
        payload?: any
    ): Observable<T> {
        return this.httpClient.patch<T>(baseApiUrl + apiRoute, payload);
    }

    public Delete<T>(baseApiUrl: string, apiRoute: string): Observable<T> {
        return this.httpClient.delete<T>(baseApiUrl + apiRoute);
    }

    public postForm<T>(path: string, object: FormData, queryParams?: Object) {
        let url = this.addQueryParams(`${path}`, queryParams);
        return this.httpClient.post<T>(url, object, this._formHttpOptions);
    }

    public patchForm<T>(path: string, object: FormData, queryParams?: Object) {
        let url = this.addQueryParams(`${path}`, queryParams);
        return this.httpClient.patch<T>(url, object, this._formHttpOptions);
    }

    private addQueryParams(url: string, obj?: object): string {
        if (obj) {
            let firstParam = !url.includes('?') ? '?' : '&';
            let params = Object.entries(obj).map(([key, val]) => {
                return val !== undefined && val !== null ? `${key}=${val}` : '';
            });
            let realParams = params.filter(x => x != '');
            let filterString = realParams.join('&');
            return url.concat(firstParam, filterString);
        } else {
            return url;
        }
    }

    public GetBlob(
        baseApiUrl: string,
        apiRoute: string,
        params?: HttpParams | any
    ): Observable<Blob> {
        const url = this.addQueryParams(`${baseApiUrl + apiRoute}`, params);
        return this.httpClient.get(url, { responseType: 'blob' });
    }
}
